import {
  Divider,
  Stack,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  Typography,
  ButtonGroup,
  Alert,
} from '@mui/material';
import { AxiosError } from 'axios';
import {
  Field,
  Form,
  FormikHelpers,
  FormikProvider,
  useFormik,
  useFormikContext,
} from 'formik';
import { RadioGroup, TextField } from 'formik-mui';
import i18next from 'i18next';
import {
  compact,
  filter,
  find,
  includes,
  isEmpty,
  map,
  omit,
  reduce,
  some,
  sortBy,
} from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { cascadesApi, shopsApi, usersApi } from 'api';
import {
  AGENTS_VALIDATION_SCHEMA,
  AgentsFormFields,
  DataWrapper,
  FormActions,
  FormControls,
  FormikArray,
  FormikCheckbox,
  FormikNullableBooleanCheckbox,
  FormikNumericField,
  FormikSelect,
  FormikSelectOption,
  FormikTextField,
  UserSelect,
  CommissionIntervalForm,
  INTERVAL_COMMISSION_VALIDATION_SCHEMA,
  HelpTooltip,
} from 'components';
import { IP_REGEX, NEW_ID } from 'constants/common.constants';
import {
  FUND_REQUEST_MAX_FEE_AMOUNT,
  FUND_REQUEST_MIN_FEE_AMOUNT,
} from 'constants/funds-requests.constants';
import { DEFAULT_ASSET_CURRENCY_CODE } from 'constants/order.constants';
import { SCI_CUSTOMER_ALTERNATIVES_VALIDATION_SCHEMA } from 'constants/shops.constants';
import {
  CustomerDataCollectionOrder,
  AppConfigArea,
  AppConfigKey,
  OrderType,
  PayinOrderTimeoutConfigKey,
  QueryKey,
  RequisitesCascadeMode,
  ShopStatus,
} from 'enums';
import {
  useCrossFiatCurrencyExchange,
  useAppConfigValue,
  useCurrencies,
  useMeta,
  useMutation,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Shop, ShopPaymentTypeConfig } from 'types';
import {
  formatUtils,
  formUtils,
  getSciDomainOptions,
  shopUtils,
  validationUtils,
} from 'utils';

import { AmountAntiFraud } from './AmountAntiFraud';
import { CustomerFieldsPayin } from './CustomerFieldsPayin';
import { DeepLinkPaymentPageSetting } from './DeepLinkPaymentPageSetting';
import { MaxParallelCustomerOrdersField } from './MaxParallelCustomerOrdersField';
import { PayinBlockAmounts } from './PayinBlockAmounts';
import { PayinBlockCustomers } from './PayinBlockCustomers';
import { SciCustomerAlternativesConfig } from './SciCustomerAlternativesConfig';
import { SciPaymentTypeConfigs } from './SciPaymentTypeConfigs';
import { ShopP2PProviders } from './ShopP2PProviders';
import { ShopP2PProvidersCurrencyExchanges } from './ShopP2PProvidersCurrencyExchanges';
import { ShopPayinFiatCurrencyExchangeConfigs } from './ShopPayinFiatCurrencyExchangeConfigs';
import { ShopPayinGroups } from './ShopPayinGroups';
import { ShopPayinTradeMethods } from './ShopPayinTradeMethods';
import { ShopPayoutGroups } from './ShopPayoutGroups';
import { ShopPayoutTradeMethods } from './ShopPayoutTradeMethods';
import { ShopStatusSelect } from '../ShopStatusSelect';

const MAX_BLOCK_DURATION_SEC = 100000; // ~28 hours for amount, ~70 days for customer

enum AmountRandomizerMode {
  Absolute = 'absolute',
  Percentage = 'percentage',
}

type MissedMappingConfig = {
  failOnMissedMapping?: boolean | null;
  failOnMissedMappingPerShopAndProvider?: boolean | null;
};

type Props = {
  shopId: string;
  navigateBack: () => void;
};

export type Values = Pick<
  Shop,
  | 'name'
  | 'webSiteUrl'
  | 'webSiteIp'
  | 'shopIpWhitelist'
  | 'status'
  | 'userId'
  | 'commission'
  | 'intervalCommissions'
  | 'payoutCommission'
  | 'assetCurrencyId'
  | 'fiatCurrencyId'
  | 'payinCurrencyExchangeId'
  | 'payinAssetCurrencyExchangeId'
  | 'payinReverseAssetCurrencyExchangeId'
  | 'payinFiatCurrencyExchangeConfigs'
  | 'payoutCurrencyExchangeId'
  | 'agents'
  | 'configs'
  | 'payinOrderMinAmount'
  | 'payinOrderMaxAmount'
  | 'maxParallelCustomerOrders'
  | 'payoutOrderMinAmount'
  | 'payoutOrderMaxAmount'
  | 'customerDataCollectionOrder'
  | 'collectCustomerReceipts'
  | 'payinTimeoutConfig'
  | 'payoutMaxTimeout'
  | 'payinTradeMethodsConfigs'
  | 'payoutTradeMethodsConfigs'
  | 'p2pProvidersConfigs'
  | 'p2pProvidersCurrencyExchanges'
  | 'payinAmountRandomizer'
  | 'payinGroupsConfigs'
  | 'sciDomainId'
  | 'displayName'
  | 'useDeepLinkPaymentPage'
  | 'payinBlockCustomersConfigs'
  | 'payinBlockAmountConfigs'
  | 'payinCreateResponseTimeout'
  | 'payinCreateSyncRequisitesResponseTimeout'
  | 'payoutCreateOrderResponseTimeout'
  | 'fullWebhook'
  | 'payinTrustedCustomersOnly'
  | 'failOnMissedMapping'
  | 'payinTSBPFallback'
  | 'payinTCard2CardFallback'
  | 'payinAmountAntiFraudRules'
  | 'payinTSimFallback'
  | 'isWithdrawalRestricted'
  | 'withdrawalFeeAmount'
  | 'isInternalTransferRestricted'
  | 'sciPaymentConfigs'
  | 'payinDistributionSameRequisites'
  | 'cascadeId'
  | 'distributionDebug'
  | 'payinTSBPNSPKTransform'
  | 'payinTCard2CardNSPKTransform'
  | 'payinTNSPKFallback'
  | 'payinNSPKInstantSyncRequisites'
  | 'useProviderPaymentUrl'
  | 'comment'
  | 'sciCustomerAlternativesConfig'
  | 'sciCustomTradeMethodsEnabled'
  | 'clarityEnabled'
>;

function SciCustomerAlternativesConfigConnected() {
  const { values } = useFormikContext<Values>();
  return (
    <SciCustomerAlternativesConfig
      payinTimeoutConfigRequisites={
        values.payinTimeoutConfig?.[PayinOrderTimeoutConfigKey.Requisites]
      }
    />
  );
}

export const ManageShop: React.FC<Props> = ({ shopId, navigateBack }) => {
  const { paymentTypes, sciDomains, tradeMethods } = useUserContext();
  const sciDomainOptions = getSciDomainOptions(sciDomains);

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();

  const {
    assetCurrenciesOptions,
    fiatCurrenciesOptions,
    currencyExchanges,
    defaultAssetCurrency,
    getAssetCurrencyCode,
    getFiatCurrencyCode,
    getCurrencyExchangeOptions,
    getDefaultCurrencyExchange,
    getAssetCurrencyExchangeOptions,
    getCurrencyExchange,
  } = useCurrencies();
  const [currencyExchangeOptions, setCurrencyExchangeOptions] = useState<
    FormikSelectOption[]
  >([]);
  const [
    payinAssetCurrencyExchangeOptions,
    setPayinAssetCurrencyExchangeOptions,
  ] = useState<FormikSelectOption[]>([]);
  const [mappedFiatCurrenciesOptions, setMappedFiatCurrenciesOptions] =
    useState<FormikSelectOption[]>([]);

  const {
    user,
    apiRole,
    isMerchant,
    isMerchantOperator,
    isAdmin,
    isAdminOrTechOperator,
  } = useUser();
  const {
    isMultitransferEnabled,
    isSciCustomerAlternativesEnabled,
    isClarityEnabled,
    isZeroFeesForbidden,
  } = useMeta();

  const missedMappingConfigQueryResult = useAppConfigValue({
    area: AppConfigArea.P2PProvidersCommon,
    key: AppConfigKey.MissedMappingConfig,
  });

  const missedMappingConfig = (missedMappingConfigQueryResult.data
    ?.jsonObjectValue ?? missedMappingConfigQueryResult.data) as
    | MissedMappingConfig
    | undefined;
  const showMissedMappingOverride =
    missedMappingConfig?.failOnMissedMappingPerShopAndProvider === true;
  const missedMappingGlobalDefault =
    (missedMappingConfig?.failOnMissedMapping ?? true) === true;
  const missedMappingGlobalDefaultLabel = t(
    missedMappingGlobalDefault
      ? 'fields.fail_on_missed_mapping_enabled'
      : 'fields.fail_on_missed_mapping_disabled',
  );
  const isNew = useMemo(() => shopId === NEW_ID, [shopId]);

  const initialState: Values = useMemo(
    () => ({
      name: '',
      userId: (isMerchant && user?.id) || '',
      webSiteUrl: '',
      webSiteIp: '',
      shopIpWhitelist: [],
      commission: (isMerchant && user?.transactionPercentage) || 0,
      payoutCommission: (isMerchant && user?.payoutTransactionPercentage) || 0,
      assetCurrencyId: assetCurrenciesOptions[0]?.value || '',
      fiatCurrencyId:
        filter(fiatCurrenciesOptions, (option) =>
          some(currencyExchanges, {
            assetCurrencyId: assetCurrenciesOptions[0]?.value || '',
            fiatCurrencyId: option.value,
          }),
        )?.[0]?.value || '',
      payinCurrencyExchangeId: '',
      payinAssetCurrencyExchangeId: '',
      payinReverseAssetCurrencyExchangeId: '',
      payoutCurrencyExchangeId: '',
      payinFiatCurrencyExchangeConfigs: [],
      configs: [],
      payinOrderMinAmount: 0,
      payinOrderMaxAmount: 0,
      maxParallelCustomerOrders: null,
      payoutOrderMinAmount: 0,
      payoutOrderMaxAmount: 0,
      collectCustomerReceipts: false,
      status: ShopStatus.Inactive, //todo: move to admin view as well and fix tests
      payinCreateResponseTimeout: null,
      payinCreateSyncRequisitesResponseTimeout: null,
      payoutCreateOrderResponseTimeout: null,
      agents: [],
      customerDataCollectionOrder: CustomerDataCollectionOrder.BeforePayment,
      sciDomainId: '',
      displayName: '',
      useDeepLinkPaymentPage: false,
      payinAmountRandomizer: {
        enabled: false,
      },
      payoutMaxTimeout: null,
      intervalCommissions: [],
      distributionDebug: false,
      payinTradeMethodsConfigs: [],
      payoutTradeMethodsConfigs: [],
      p2pProvidersConfigs: [],
      payinGroupsConfigs: [],
      p2pProvidersCurrencyExchanges: [],
      fullWebhook: false,
      payinTrustedCustomersOnly: false,
      failOnMissedMapping: null,
      payinTSBPFallback: false,
      payinTCard2CardFallback: false,
      payinTSimFallback: false,
      payinTSBPNSPKTransform: false,
      payinTCard2CardNSPKTransform: false,
      payinTNSPKFallback: false,
      payinNSPKInstantSyncRequisites: false,
      useProviderPaymentUrl: false,
      payinAmountAntiFraudRules: [],
      isWithdrawalRestricted: false,
      isInternalTransferRestricted: false,
      sciPaymentConfigs: [],
      payinDistributionSameRequisites: false,
      cascadeId: '',
      comment: '',
      sciCustomerAlternativesConfig: {
        alternativeAmountEnabled: false,
        alternativeTradeMethodEnabled: false,
      },
      sciCustomTradeMethodsEnabled: false,
      clarityEnabled: false,
    }),
    [
      user,
      assetCurrenciesOptions,
      isMerchant,
      fiatCurrenciesOptions,
      currencyExchanges,
    ],
  );

  const validationSchema = useMemo(() => {
    const shopBlockSettingsConfigSchema = Yup.array().of(
      Yup.object().shape({
        period: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        count: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        duration: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .lessThan(
            MAX_BLOCK_DURATION_SEC,
            `${tCommon('errors.max')} ${MAX_BLOCK_DURATION_SEC}`,
          )
          .required(tCommon('errors.required')),
      }),
    );
    return Yup.object().shape({
      name: Yup.string().required(tCommon('errors.required')),
      webSiteUrl: Yup.string().required(tCommon('errors.required')),
      webSiteIp: Yup.string().matches(IP_REGEX, tCommon('errors.ip')),
      shopIpWhitelist: Yup.array().of(
        Yup.string()
          .required(i18next.t('errors.required'))
          .matches(IP_REGEX, tCommon('errors.ip')),
      ),
      assetCurrencyId: Yup.string().required(tCommon('errors.required')),
      fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
      payinCurrencyExchangeId: Yup.string().nullable(),
      payinAssetCurrencyExchangeId: Yup.string().nullable().optional(),
      payinReverseAssetCurrencyExchangeId: Yup.string().nullable().optional(),
      payoutCurrencyExchangeId: Yup.string().nullable(),
      fullWebhook: Yup.boolean().nullable(),
      payinTrustedCustomersOnly: Yup.boolean().nullable(),
      failOnMissedMapping: Yup.boolean().nullable(),
      payinTSBPFallback: Yup.boolean().nullable(),
      payinTCard2CardFallback: Yup.boolean().nullable(),
      payinTSimFallback: Yup.boolean().nullable(),
      payinTSBPNSPKTransform: Yup.boolean().nullable(),
      payinTCard2CardNSPKTransform: Yup.boolean().nullable(),
      payinTNSPKFallback: Yup.boolean().nullable(),
      payinNSPKInstantSyncRequisites: Yup.boolean().nullable(),
      useProviderPaymentUrl: Yup.boolean().nullable(),
      useDeepLinkPaymentPage: Yup.boolean().nullable(),
      commission: Yup.number()
        .min(0, tCommon('errors.percentage'))
        .max(100, tCommon('errors.percentage'))
        .test(
          'zero-fee-forbidden',
          tCommon('errors.zero_fee_forbidden'),
          (value, context) =>
            !isZeroFeesForbidden ||
            context.parent.status !== ShopStatus.Active ||
            !!value,
        ),
      payoutCommission: Yup.number()
        .min(0, tCommon('errors.percentage'))
        .max(100, tCommon('errors.percentage'))
        .test(
          'zero-fee-forbidden',
          tCommon('errors.zero_fee_forbidden'),
          (value, context) =>
            !isZeroFeesForbidden ||
            context.parent.status !== ShopStatus.Active ||
            !!value,
        ),
      configs: Yup.array().of(
        Yup.object().shape({
          paymentTypeId: Yup.string().required(tCommon('errors.required')),
        }),
      ),
      displayName: Yup.string().nullable().optional(),
      payinCreateResponseTimeout: Yup.number().optional().nullable(),
      payinCreateSyncRequisitesResponseTimeout: Yup.number()
        .optional()
        .nullable(),
      payoutCreateOrderResponseTimeout: Yup.number().optional().nullable(),
      ...(isAdminOrTechOperator && {
        userId: Yup.string().required(tCommon('errors.required')),
        payinFiatCurrencyExchangeConfigs: Yup.array().of(
          Yup.object().shape({
            fiatCurrencyExchangeId: Yup.string().required(
              tCommon('errors.required'),
            ),
            minSpread: Yup.number()
              .moreThan(-100, tCommon('errors.percentage'))
              .lessThan(100, tCommon('errors.percentage'))
              .nullable(),
            maxSpread: Yup.number()
              .moreThan(-100, tCommon('errors.percentage'))
              .lessThan(100, tCommon('errors.percentage'))
              .nullable(),
            traderSpreadCompensation: Yup.boolean(),
          }),
        ),
        payinTradeMethodsConfigs: Yup.array().of(
          Yup.object().shape({
            tradeMethodId: Yup.string().required(tCommon('errors.required')),
            anyBank: Yup.boolean(),
          }),
        ),
        payoutTradeMethodsConfigs: Yup.array().of(
          Yup.object().shape({
            tradeMethodId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        p2pProvidersConfigs: Yup.array().of(
          Yup.object().shape({
            p2pProviderId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        payinGroupsConfigs: Yup.array().of(
          Yup.object().shape({
            groupId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        p2pProvidersCurrencyExchanges: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        sciPaymentConfigs: Yup.array().of(
          Yup.object().shape({
            paymentTypeId: Yup.string().required(tCommon('errors.required')),
            bankId: Yup.string().optional().nullable(),
          }),
        ),
        payinBlockCustomersConfigs: shopBlockSettingsConfigSchema,
        payinBlockAmountConfigs: shopBlockSettingsConfigSchema,
        payoutMaxTimeout: Yup.number().optional().nullable(),
        ...AGENTS_VALIDATION_SCHEMA,
        ...validationUtils.getNaturalNumberRangeValidationSchema(
          'payinOrderMinAmount',
          'payinOrderMaxAmount',
        ),
        maxParallelCustomerOrders:
          validationUtils.getNaturalNumberFieldValidationSchema({
            integer: true,
          }),
        ...validationUtils.getNaturalNumberRangeValidationSchema(
          'payoutOrderMinAmount',
          'payoutOrderMaxAmount',
        ),
        intervalCommissions: INTERVAL_COMMISSION_VALIDATION_SCHEMA,
        payinAmountAntiFraudRules: Yup.array().of(
          Yup.object().shape({
            minAmount: Yup.number().when('maxAmount', ([maxAmount], schema) =>
              maxAmount
                ? schema.optional().nullable()
                : schema.required(tCommon('errors.required')),
            ),
            maxAmount: Yup.number().nullable().optional(),
            paymentTypeId: Yup.string().nullable().optional(),
            expiredAt: Yup.string().nullable().optional(),
          }),
        ),
        withdrawalFeeAmount: Yup.number()
          .min(
            FUND_REQUEST_MIN_FEE_AMOUNT,
            tCommon('errors.natural_or_zero_number'),
          )
          .max(
            FUND_REQUEST_MAX_FEE_AMOUNT,
            `${tCommon('errors.max')} ${FUND_REQUEST_MAX_FEE_AMOUNT}`,
          )
          .optional()
          .nullable(),
        comment: Yup.string().optional().nullable(),
        sciCustomerAlternativesConfig:
          SCI_CUSTOMER_ALTERNATIVES_VALIDATION_SCHEMA,
      }),
    });
  }, [tCommon, isAdminOrTechOperator, isZeroFeesForbidden]);

  const [initialValues, setInitialValues] = useState<Values>(initialState);

  const normalizeConfigs = useCallback(
    (configs: ShopPaymentTypeConfig[]) =>
      map(configs, (config) => {
        const customerFields = find(paymentTypes, {
          id: config.paymentTypeId,
        })?.customerFields;

        config.customerFields = sortBy(
          map(
            customerFields,
            (field) =>
              find(config.customerFields, { name: field.name }) || {
                name: field.name,
                hidden: field.hidden,
              },
          ),
          'hidden',
        );
        return config;
      }),
    [paymentTypes],
  );

  const getSortedPayinGroupsConfigs = useCallback((shop: Shop) => {
    const payinGroups = filter(shop.groups, (group) => group.payin);
    const groupsWithPriority = payinGroups.map((group) => {
      const config = find(
        shop.payinGroupsConfigs,
        (config) => config.groupId === group.id,
      );

      return {
        id: config?.id,
        groupId: group.id,
        priority: config?.priority || 0,
      };
    });
    return sortBy(groupsWithPriority, (group) => -group.priority);
  }, []);

  const queryClient = useQueryClient();
  const queryResult = useQuery(
    [QueryKey.Shops, shopId],
    () => shopsApi.getOneAsRole(apiRole)(shopId),
    {
      enabled: !!shopId && !isNew,
      onSuccess: (data) => {
        setInitialValues({
          name: data.name,
          userId: data.userId,
          webSiteUrl: data.webSiteUrl,
          webSiteIp: data.webSiteIp,
          shopIpWhitelist: data.shopIpWhitelist || [],
          assetCurrencyId: data.assetCurrencyId,
          fiatCurrencyId: data.fiatCurrencyId,
          payinCurrencyExchangeId: data.payinCurrencyExchangeId || '',
          payinAssetCurrencyExchangeId: data.payinAssetCurrencyExchangeId || '',
          payinReverseAssetCurrencyExchangeId:
            data.payinReverseAssetCurrencyExchangeId || '',
          payoutCurrencyExchangeId: data.payoutCurrencyExchangeId || '',
          payinFiatCurrencyExchangeConfigs:
            data.payinFiatCurrencyExchangeConfigs || [],
          commission: data.commission,
          intervalCommissions: data.intervalCommissions || [],
          payoutCommission: data.payoutCommission,
          status: data.status,
          configs: normalizeConfigs(data.configs),
          agents: data.agents,
          customerDataCollectionOrder: data.customerDataCollectionOrder,
          sciDomainId: data.sciDomainId || '',
          displayName: data.displayName || '',
          useDeepLinkPaymentPage: data.useDeepLinkPaymentPage || false,
          payinAmountRandomizer: data.payinAmountRandomizer || {
            enabled: false,
          },
          distributionDebug: data.distributionDebug,
          payinOrderMinAmount: data.payinOrderMinAmount,
          payinOrderMaxAmount: data.payinOrderMaxAmount,
          maxParallelCustomerOrders: data.maxParallelCustomerOrders ?? 0,
          payoutOrderMinAmount: data.payoutOrderMinAmount,
          payoutOrderMaxAmount: data.payoutOrderMaxAmount,
          collectCustomerReceipts: data.collectCustomerReceipts,
          payinTimeoutConfig: data.payinTimeoutConfig,
          payoutMaxTimeout: data.payoutMaxTimeout,
          payinCreateResponseTimeout: data.payinCreateResponseTimeout,
          payinCreateSyncRequisitesResponseTimeout:
            data.payinCreateSyncRequisitesResponseTimeout,
          payoutCreateOrderResponseTimeout:
            data.payoutCreateOrderResponseTimeout,
          payinTradeMethodsConfigs: data.payinTradeMethodsConfigs,
          payoutTradeMethodsConfigs: data.payoutTradeMethodsConfigs,
          p2pProvidersConfigs: sortBy(data.p2pProvidersConfigs, 'priority'),
          payinGroupsConfigs: getSortedPayinGroupsConfigs(data),
          p2pProvidersCurrencyExchanges: data.p2pProvidersCurrencyExchanges,
          payinBlockCustomersConfigs: data.payinBlockCustomersConfigs,
          payinBlockAmountConfigs: data.payinBlockAmountConfigs,
          fullWebhook: data.fullWebhook || false,
          payinTrustedCustomersOnly: data.payinTrustedCustomersOnly || false,
          failOnMissedMapping: data.failOnMissedMapping ?? null,
          payinAmountAntiFraudRules: data.payinAmountAntiFraudRules || [],
          isWithdrawalRestricted: data.isWithdrawalRestricted || false,
          withdrawalFeeAmount: data.withdrawalFeeAmount,
          isInternalTransferRestricted:
            data.isInternalTransferRestricted || false,
          sciPaymentConfigs: data.sciPaymentConfigs || [],
          payinDistributionSameRequisites:
            data.payinDistributionSameRequisites || false,
          cascadeId: data.cascadeId || '',
          payinTSBPFallback: data.payinTSBPFallback || false,
          payinTCard2CardFallback: data.payinTCard2CardFallback || false,
          payinTSimFallback: data.payinTSimFallback || false,
          payinTSBPNSPKTransform: data.payinTSBPNSPKTransform || false,
          payinTCard2CardNSPKTransform:
            data.payinTCard2CardNSPKTransform || false,
          payinTNSPKFallback: data.payinTNSPKFallback || false,
          payinNSPKInstantSyncRequisites:
            data.payinNSPKInstantSyncRequisites || false,
          useProviderPaymentUrl: data.useProviderPaymentUrl || false,
          comment: data.comment ?? '',
          sciCustomerAlternativesConfig: data.sciCustomerAlternativesConfig || {
            alternativeAmountEnabled: false,
            alternativeTradeMethodEnabled: false,
          },
          sciCustomTradeMethodsEnabled:
            data.sciCustomTradeMethodsEnabled || false,
          clarityEnabled: data.clarityEnabled || false,
        });
      },
      keepPreviousData: true,
    },
  );

  const payinGroups = useMemo(
    () => filter(queryResult.data?.groups, (group) => group.payin),
    [queryResult],
  );

  const payoutGroups = useMemo(
    () =>
      sortBy(
        filter(queryResult.data?.groups, (group) => group.payout),
        (group) => group.payoutOrdersAccessTimeout,
      ),
    [queryResult],
  );

  const queryResultTraders = useQuery(
    QueryKey.UsersTraders,
    usersApi.getAllTraders,
    { enabled: isAdminOrTechOperator },
  );

  const queryResultMerchants = useQuery(
    QueryKey.UsersMerchants,
    usersApi.getAllMerchants,
    { enabled: isAdminOrTechOperator },
  );

  const cascadesQuery = useQuery(QueryKey.Cascades, cascadesApi.getAll, {
    enabled: isAdminOrTechOperator,
  });

  const cascadeOptions = useMemo(
    () => formUtils.getOptions(cascadesQuery.data || [], 'name', 'id'),
    [cascadesQuery.data],
  );

  const { mutate: createShop } = useMutation<Shop, AxiosError, Partial<Shop>>(
    shopsApi.createAsRole(apiRole),
  );
  const { mutate: updateShop } = useMutation<
    Shop,
    AxiosError,
    { id: string; data: Values }
  >(shopsApi.updateAsRole(apiRole));

  const handleSubmit = useCallback(
    (shop: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: () => {
          queryClient.invalidateQueries(QueryKey.Shops);
          navigateBack();
        },
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error)),
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      formikHelpers.setSubmitting(true);

      if (!isAdmin) {
        shop = omit(shop, 'withdrawalFeeAmount');
      }
      if (isMerchant || isMerchantOperator) {
        shop = omit(
          shop,
          'payinOrderMinAmount',
          'payinOrderMaxAmount',
          'maxParallelCustomerOrders',
          'payoutOrderMinAmount',
          'payoutOrderMaxAmount',
          'payinTimeoutConfig',
          'payoutMaxTimeout',
          'payinFiatCurrencyExchangeConfigs',
          'payinAssetCurrencyExchangeId',
          'payinReverseAssetCurrencyExchangeId',
          'payinTradeMethodsConfigs',
          'payinAmountRandomizer',
          'payoutTradeMethodsConfigs',
          'p2pProvidersConfigs',
          'payinGroupsConfigs',
          'sciDomainId',
          'useDeepLinkPaymentPage',
          'p2pProvidersCurrencyExchanges',
          'payinBlockCustomersConfigs',
          'payinBlockAmountConfigs',
          'payinCreateResponseTimeout',
          'payoutCreateOrderResponseTimeout',
          'payinCreateSyncRequisitesResponseTimeout',
          'fullWebhook',
          'customerDataCollectionOrder',
          'agents',
          'payinTrustedCustomersOnly',
          'failOnMissedMapping',
          'payinAmountAntiFraudRules',
          'isWithdrawalRestricted',
          'isInternalTransferRestricted',
          'sciPaymentConfigs',
          'payinDistributionSameRequisites',
          'distributionDebug',
          'userId',
          'payinTSBPFallback',
          'payinTCard2CardFallback',
          'payinTSimFallback',
          'payinTSBPNSPKTransform',
          'payinTCard2CardNSPKTransform',
          'payinTNSPKFallback',
          'payinNSPKInstantSyncRequisites',
          'useProviderPaymentUrl',
          'comment',
          'sciCustomerAlternativesConfig',
          'sciCustomTradeMethodsEnabled',
          'clarityEnabled',
        );
      }

      if (isNew) {
        shop = omit(shop, 'payinGroupsConfigs');
      } else {
        shop = omit(shop, 'fiatCurrencyId', 'userId');
      }

      if (isNew) {
        createShop(shop, options);
      } else {
        updateShop({ id: shopId, data: shop }, options);
      }
    },
    [
      isNew,
      queryClient,
      navigateBack,
      isMerchant,
      isMerchantOperator,
      isAdmin,
      createShop,
      updateShop,
      shopId,
    ],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  const [cascadeModeEnabled, setCascadeModeEnabled] = useState(false);

  const showPayoutCascadeTimeoutTotalWarning = useMemo(() => {
    if (!cascadeModeEnabled) {
      return false;
    }

    const payoutTimeout = formik.values.payoutMaxTimeout;

    if (!payoutTimeout) {
      return false;
    }

    const selectedCascade = find(cascadesQuery.data, {
      id: formik.values.cascadeId,
    });

    if (
      !selectedCascade ||
      selectedCascade?.payoutCascadeMode !==
        RequisitesCascadeMode.SequentialAssignees
    ) {
      return false;
    }

    const cascadeTotal = reduce(
      selectedCascade.payoutOrderAssignees,
      (total, assignee) => {
        total += assignee.timeout;
        return total;
      },
      0,
    );

    return cascadeTotal > payoutTimeout;
  }, [
    cascadeModeEnabled,
    cascadesQuery.data,
    formik.values.cascadeId,
    formik.values.payoutMaxTimeout,
  ]);

  useEffect(() => {
    setCascadeModeEnabled(!!initialValues.cascadeId);
  }, [initialValues.cascadeId]);

  const isCascadeMode = useMemo(() => cascadeModeEnabled, [cascadeModeEnabled]);

  const handleToggleCascadeMode = useCallback(() => {
    const newMode = !cascadeModeEnabled;
    setCascadeModeEnabled(newMode);

    if (!newMode) {
      formik.setFieldValue('cascadeId', '');
    }
  }, [cascadeModeEnabled, formik]);

  const { crossFiatCurrencyExchange } = useCrossFiatCurrencyExchange({
    enabled: isAdminOrTechOperator,
    fiatCurrencyId: formik.values.fiatCurrencyId,
  });

  const shopFiatCurrencyCode = useMemo(
    () => getFiatCurrencyCode(formik.values.fiatCurrencyId),
    [formik.values.fiatCurrencyId, getFiatCurrencyCode],
  );
  const shopAssetCurrenciesOptions = useMemo(() => {
    const currentAssetCurrencyId = formik.values.assetCurrencyId;
    if (
      !currentAssetCurrencyId ||
      find(assetCurrenciesOptions, { value: currentAssetCurrencyId })
    ) {
      return assetCurrenciesOptions;
    }

    return [
      ...assetCurrenciesOptions,
      {
        value: currentAssetCurrencyId,
        label:
          getAssetCurrencyCode(currentAssetCurrencyId) ||
          currentAssetCurrencyId,
      },
    ];
  }, [
    assetCurrenciesOptions,
    formik.values.assetCurrencyId,
    getAssetCurrencyCode,
  ]);

  useEffect(() => {
    const assetCurrencyId = isNew
      ? initialState.assetCurrencyId
      : queryResult.data?.assetCurrencyId;
    const fiatCurrencyId = isNew
      ? initialState.fiatCurrencyId
      : queryResult.data?.fiatCurrencyId;
    const shouldUpdate = assetCurrencyId && fiatCurrencyId;

    if (shouldUpdate) {
      setCurrencyExchangeOptions(
        getCurrencyExchangeOptions({
          assetCurrencyId,
          fiatCurrencyId,
        }),
      );
      setPayinAssetCurrencyExchangeOptions(
        getAssetCurrencyExchangeOptions(assetCurrencyId),
      );
    }
  }, [
    isNew,
    initialState.assetCurrencyId,
    initialState.fiatCurrencyId,
    queryResult.data?.assetCurrencyId,
    queryResult.data?.fiatCurrencyId,
    getCurrencyExchangeOptions,
    getAssetCurrencyExchangeOptions,
  ]);

  useEffect(() => {
    if (queryResult.data?.assetCurrencyId && queryResult.data?.fiatCurrencyId) {
      setCurrencyExchangeOptions(
        getCurrencyExchangeOptions({
          assetCurrencyId: queryResult.data.assetCurrencyId,
          fiatCurrencyId: queryResult.data.fiatCurrencyId,
        }),
      );
      setPayinAssetCurrencyExchangeOptions(
        getAssetCurrencyExchangeOptions(queryResult.data.assetCurrencyId),
      );
    }
  }, [
    queryResult.data?.assetCurrencyId,
    queryResult.data?.fiatCurrencyId,
    getCurrencyExchangeOptions,
    getAssetCurrencyExchangeOptions,
  ]);

  const isCurrencyExchangeSelectDisabled = useMemo(
    () => currencyExchangeOptions.length === 1,
    [currencyExchangeOptions],
  );

  useEffect(() => {
    const assetCurrencyId = isNew
      ? initialState.assetCurrencyId
      : queryResult.data?.assetCurrencyId;
    const shouldUpdate = !!assetCurrencyId;
    if (shouldUpdate) {
      setMappedFiatCurrenciesOptions(
        filter(fiatCurrenciesOptions, (option) =>
          some(currencyExchanges, {
            assetCurrencyId,
            fiatCurrencyId: option.value,
          }),
        ),
      );
    }
  }, [
    isNew,
    initialState.assetCurrencyId,
    queryResult.data?.assetCurrencyId,
    fiatCurrenciesOptions,
    currencyExchanges,
  ]);

  const isFiatSelectDisabled = useMemo(
    () => mappedFiatCurrenciesOptions.length === 1 || !isNew,
    [mappedFiatCurrenciesOptions, isNew],
  );

  const handleCurrencyChange = useCallback(
    (value: string, name: string) => {
      const isAssetCurrencyChanged = name === 'assetCurrencyId';
      const assetCurrencyId = isAssetCurrencyChanged
        ? value
        : formik.values.assetCurrencyId;
      const isFiatCurrencyChanged = name === 'fiatCurrencyId';
      let fiatCurrencyId = isFiatCurrencyChanged
        ? value
        : formik.values.fiatCurrencyId;

      if (isAssetCurrencyChanged) {
        const newMappedFiatCurrenciesOptions = filter(
          fiatCurrenciesOptions,
          (option) =>
            some(currencyExchanges, {
              assetCurrencyId,
              fiatCurrencyId: option.value,
            }),
        );

        if (assetCurrencyId !== formik.initialValues.assetCurrencyId) {
          if (
            !find(
              newMappedFiatCurrenciesOptions,
              ({ value }) => value === fiatCurrencyId,
            )
          ) {
            fiatCurrencyId = newMappedFiatCurrenciesOptions[0].value;
            formik.setFieldValue('fiatCurrencyId', fiatCurrencyId);
          }
        } else {
          fiatCurrencyId = formik.initialValues.fiatCurrencyId;
          formik.setFieldValue('fiatCurrencyId', fiatCurrencyId);
        }

        setMappedFiatCurrenciesOptions(newMappedFiatCurrenciesOptions);
      }

      if (assetCurrencyId && fiatCurrencyId) {
        setCurrencyExchangeOptions(
          getCurrencyExchangeOptions({
            assetCurrencyId,
            fiatCurrencyId,
          }),
        );
      }

      if (isFiatCurrencyChanged) {
        formik.setFieldValue('payinFiatCurrencyExchangeConfigs', []);
        formik.setFieldValue('payinAssetCurrencyExchangeId', '');
        formik.setFieldValue('payinReverseAssetCurrencyExchangeId', '');
        formik.setFieldValue('payinTradeMethodsConfigs', []);
        formik.setFieldValue('payoutTradeMethodsConfigs', []);
        formik.setFieldValue('p2pProvidersConfigs', []);
        formik.setFieldValue('p2pProvidersCurrencyExchanges', []);
      }

      if (
        assetCurrencyId !== formik.initialValues.assetCurrencyId ||
        fiatCurrencyId !== formik.initialValues.fiatCurrencyId
      ) {
        // if merchant modify currency - drop selected exchange
        // if admin modify currency - set default exchange
        const defaultCurrencyExchangeId =
          (isAdminOrTechOperator &&
            getDefaultCurrencyExchange(assetCurrencyId, fiatCurrencyId)?.id) ||
          '';

        formik.setFieldValue(
          'payinCurrencyExchangeId',
          defaultCurrencyExchangeId,
        );
        formik.setFieldValue(
          'payoutCurrencyExchangeId',
          defaultCurrencyExchangeId,
        );
      } else {
        formik.setFieldValue(
          'payinCurrencyExchangeId',
          formik.initialValues.payinCurrencyExchangeId,
        );
        formik.setFieldValue(
          'payoutCurrencyExchangeId',
          formik.initialValues.payoutCurrencyExchangeId,
        );
      }
    },
    [
      formik,
      isAdminOrTechOperator,
      currencyExchanges,
      fiatCurrenciesOptions,
      getCurrencyExchangeOptions,
      getDefaultCurrencyExchange,
    ],
  );

  const crossFiatCurrencyIds = useMemo(
    () =>
      compact(
        map(
          formik.values.payinFiatCurrencyExchangeConfigs,
          (config) =>
            find(crossFiatCurrencyExchange, {
              id: config.fiatCurrencyExchangeId,
            })?.crossFiatCurrencyId!,
        ),
      ),
    [formik.values.payinFiatCurrencyExchangeConfigs, crossFiatCurrencyExchange],
  );

  const handlePayinFiatCurrencyExchangeChange = useCallback(() => {
    if (formik.values.payinTradeMethodsConfigs?.length) {
      const fiatCurrencyId = formik.values.fiatCurrencyId;
      const tradeMethodIds = fiatCurrencyId
        ? reduce(
            tradeMethods,
            (acc, tradeMethod) => {
              if (
                includes(
                  [fiatCurrencyId, ...crossFiatCurrencyIds],
                  tradeMethod.fiatCurrencyId,
                )
              ) {
                acc.push(tradeMethod.id);
              }
              return acc;
            },
            [] as string[],
          )
        : [];
      formik.setFieldValue(
        'payinTradeMethodsConfigs',
        filter(formik.values.payinTradeMethodsConfigs, ({ tradeMethodId }) =>
          tradeMethodIds.includes(tradeMethodId),
        ),
      );
    }
  }, [formik, tradeMethods, crossFiatCurrencyIds]);

  const renderPayinTimeoutInput = useCallback(
    (status: PayinOrderTimeoutConfigKey) => (
      <FormikNumericField
        name={`payinTimeoutConfig.${status}`}
        label={shopUtils.getPayinOrderTimeoutLabel(status)}
        allowNegative={false}
        decimalScale={0}
        disabled={!isAdminOrTechOperator}
      />
    ),
    [isAdminOrTechOperator],
  );

  const getAddedP2PProvidersAssetCurrencies = useCallback(
    (values: Values) => [
      ...(values.assetCurrencyId ? [values.assetCurrencyId] : []),
      ...filter(
        map(
          formik.values.p2pProvidersCurrencyExchanges,
          (exchange) => getCurrencyExchange(exchange.id)?.assetCurrencyId || '',
        ),
        (id) => !!id,
      ),
    ],
    [formik.values.p2pProvidersCurrencyExchanges, getCurrencyExchange],
  );

  const isPayinAmountRandomizerPercentageMode = useMemo(
    () =>
      formik.values.payinAmountRandomizer?.mode ===
      AmountRandomizerMode.Percentage,
    [formik.values.payinAmountRandomizer?.mode],
  );

  const toggleAmountRandomizerMode = useCallback(() => {
    if (isPayinAmountRandomizerPercentageMode) {
      formik.setFieldValue(
        'payinAmountRandomizer.mode',
        AmountRandomizerMode.Absolute,
      );
    } else {
      formik.setFieldValue(
        'payinAmountRandomizer.mode',
        AmountRandomizerMode.Percentage,
      );
    }
  }, [formik, isPayinAmountRandomizerPercentageMode]);

  const payinAmountRandomizerBoundsSuffix = useMemo(
    () => (isPayinAmountRandomizerPercentageMode ? '%' : shopFiatCurrencyCode),
    [isPayinAmountRandomizerPercentageMode, shopFiatCurrencyCode],
  );

  const renderIpWhiteListComponent = useCallback(
    ({ index }: { index: number }) => (
      <FormikTextField
        component={TextField}
        label={t('fields.website_ip')}
        name={`shopIpWhitelist[${index}]`}
        size="small"
        fullWidth
      />
    ),
    [t],
  );

  const payinTradeMethods = useMemo(() => {
    const payinTradeMethodIds = map(
      formik.values.payinTradeMethodsConfigs,
      (tradeMethod) => tradeMethod.tradeMethodId,
    );
    return filter(tradeMethods, (tradeMethod) =>
      includes(payinTradeMethodIds, tradeMethod.id),
    );
  }, [formik.values.payinTradeMethodsConfigs, tradeMethods]);

  const isDataLoaded = useMemo(() => formik.values.name, [formik.values.name]);

  return (
    <Fragment>
      <FormikProvider value={formik}>
        <DataWrapper queryResult={!isNew ? queryResult : undefined}>
          <div className="tw-max-w-lg">
            <Form>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControls>
                    <Typography variant="subtitle1">
                      {t('sections.common')}
                    </Typography>
                    {isAdminOrTechOperator && (
                      <Fragment>
                        <ShopStatusSelect />
                        <Stack spacing={3}>
                          <FormikNumericField
                            label={t('fields.commission')}
                            name="commission"
                            allowNegative={false}
                            percentageSuffix
                            required
                            fullWidth
                          />
                          <CommissionIntervalForm name="intervalCommissions" />
                          <FormikNumericField
                            label={t('fields.payout_commission')}
                            name="payoutCommission"
                            allowNegative={false}
                            percentageSuffix
                            required
                            fullWidth
                          />
                        </Stack>
                        <Divider />
                        <UserSelect
                          name="userId"
                          label={t('fields.merchant')}
                          users={queryResultMerchants?.data}
                          required
                          disabled={!isNew}
                        />
                      </Fragment>
                    )}
                    <Field
                      component={TextField}
                      label={t('fields.name')}
                      name="name"
                      size="small"
                      required
                      fullWidth
                    />
                    <Field
                      component={TextField}
                      label={t('fields.website_url')}
                      name="webSiteUrl"
                      size="small"
                      required
                      fullWidth
                    />
                    <Field
                      component={TextField}
                      label={t('fields.website_ip')}
                      name="webSiteIp"
                      size="small"
                      fullWidth
                    />
                    <Typography variant="subtitle1">
                      {t('fields.ip_whitelist_config')}
                    </Typography>
                    <FormikArray
                      addItemLabel={t('fields.add_ip_btn')}
                      name="shopIpWhitelist"
                      removable
                      ItemComponent={renderIpWhiteListComponent}
                    />
                    <FormikSelect
                      label={t('fields.currency')}
                      name="assetCurrencyId"
                      options={shopAssetCurrenciesOptions}
                      onChange={(value) =>
                        handleCurrencyChange(value, 'assetCurrencyId')
                      }
                      showMetaError
                      disabled={!isNew}
                    />
                    <FormikSelect
                      label={t('fields.fiat_currency')}
                      name="fiatCurrencyId"
                      options={mappedFiatCurrenciesOptions}
                      onChange={(value) =>
                        handleCurrencyChange(value, 'fiatCurrencyId')
                      }
                      disabled={isFiatSelectDisabled}
                    />
                    {isAdminOrTechOperator && (
                      <Fragment>
                        {formik.values.assetCurrencyId &&
                          formik.values.assetCurrencyId !==
                            defaultAssetCurrency?.id && (
                            <Fragment>
                              <FormikSelect
                                label={t(
                                  'fields.payin_asset_currency_exchange',
                                )}
                                name="payinAssetCurrencyExchangeId"
                                helperText={t(
                                  'fields.payin_asset_currency_exchange_description',
                                )}
                                options={payinAssetCurrencyExchangeOptions}
                                noneOption
                              />
                              <FormikSelect
                                label={t(
                                  'fields.payin_reverse_asset_currency_exchange',
                                )}
                                name="payinReverseAssetCurrencyExchangeId"
                                helperText={t(
                                  'fields.payin_reverse_asset_currency_exchange_description',
                                )}
                                options={payinAssetCurrencyExchangeOptions}
                                noneOption
                              />
                            </Fragment>
                          )}
                        {!isEmpty(currencyExchangeOptions) && (
                          <Fragment>
                            <FormikSelect
                              label={t('fields.payin_currency_exchange')}
                              name="payinCurrencyExchangeId"
                              options={currencyExchangeOptions}
                              noneOption
                              disabled={isCurrencyExchangeSelectDisabled}
                            />
                            <FormikSelect
                              label={t('fields.payout_currency_exchange')}
                              name="payoutCurrencyExchangeId"
                              options={currencyExchangeOptions}
                              noneOption
                              disabled={isCurrencyExchangeSelectDisabled}
                            />
                          </Fragment>
                        )}
                        <Grid container spacing={12}>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <FormControls>
                              <Typography variant="subtitle1">
                                {t('sections.sci_configuration.title')}
                              </Typography>
                              <FormikSelect
                                label={t('fields.sci_domain')}
                                name="sciDomainId"
                                options={sciDomainOptions}
                                noneOption
                              />
                              <Field
                                component={TextField}
                                label={t('fields.display_name')}
                                name="displayName"
                                size="small"
                                fullWidth
                              />
                              <DeepLinkPaymentPageSetting />
                              {isClarityEnabled && (
                                <FormikCheckbox
                                  name="clarityEnabled"
                                  label={t('fields.clarity_enabled')}
                                  helperText={t(
                                    'fields.clarity_enabled_helper',
                                  )}
                                />
                              )}
                              <Divider>{t('fields.sci_payment_types')}</Divider>
                              <SciPaymentTypeConfigs />
                            </FormControls>
                          </Grid>
                        </Grid>
                        <ShopPayinFiatCurrencyExchangeConfigs
                          fiatCurrencyId={formik.values.fiatCurrencyId || ''}
                          onChange={handlePayinFiatCurrencyExchangeChange}
                        />
                        <AgentsFormFields
                          formError={formik.errors['agents']}
                          agents={formik.values.agents}
                          users={queryResultTraders.data}
                          headerClassName="tw-text-base"
                        />
                        <AmountAntiFraud
                          orderType={OrderType.Payin}
                          tradeMethods={payinTradeMethods}
                        />
                        {isSciCustomerAlternativesEnabled && (
                          <Fragment>
                            <Divider>
                              {t('sections.sci_customer_alternatives.title')}
                            </Divider>
                            <SciCustomerAlternativesConfigConnected />
                          </Fragment>
                        )}
                      </Fragment>
                    )}
                    {(isAdminOrTechOperator || !isNew) && (
                      <Fragment>
                        <Divider>
                          {t('sections.payin_order_amount_limits')}
                        </Divider>
                        <FormControls row>
                          <FormikNumericField
                            name="payinOrderMinAmount"
                            label={tCommon('common.min')}
                            fullWidth
                            disabled={!isAdminOrTechOperator}
                            suffix={shopFiatCurrencyCode}
                            allowNegative={false}
                          />
                          <FormikNumericField
                            name="payinOrderMaxAmount"
                            label={tCommon('common.max')}
                            fullWidth
                            disabled={!isAdminOrTechOperator}
                            suffix={shopFiatCurrencyCode}
                            allowNegative={false}
                          />
                        </FormControls>
                        <Divider>
                          {t('sections.payout_order_amount_limits')}
                        </Divider>
                        <FormControls row>
                          <FormikNumericField
                            name="payoutOrderMinAmount"
                            label={tCommon('common.min')}
                            fullWidth
                            disabled={!isAdminOrTechOperator}
                            suffix={shopFiatCurrencyCode}
                            allowNegative={false}
                          />
                          <FormikNumericField
                            name="payoutOrderMaxAmount"
                            label={tCommon('common.max')}
                            fullWidth
                            disabled={!isAdminOrTechOperator}
                            suffix={shopFiatCurrencyCode}
                            allowNegative={false}
                          />
                        </FormControls>
                        <Divider sx={{ my: 4 }}>
                          {t('fields.status_timeouts_config')}
                        </Divider>
                        <Grid container spacing={6}>
                          <Grid size={{ xs: 6, md: 6 }}>
                            <FormControls>
                              <Typography>{tCommon('common.payin')}</Typography>
                              {renderPayinTimeoutInput(
                                PayinOrderTimeoutConfigKey.New,
                              )}
                              {renderPayinTimeoutInput(
                                PayinOrderTimeoutConfigKey.Requisites,
                              )}
                              {renderPayinTimeoutInput(
                                PayinOrderTimeoutConfigKey.Payment,
                              )}
                            </FormControls>
                          </Grid>
                          <Grid size={{ xs: 6, md: 6 }}>
                            <FormControls>
                              <Typography>
                                {tCommon('common.payout')}
                              </Typography>
                              <FormikNumericField
                                name={'payoutMaxTimeout'}
                                label={t('fields.payout_max_timeout')}
                                allowNegative={false}
                                decimalScale={0}
                                disabled={!isAdminOrTechOperator}
                              />
                            </FormControls>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 4 }}>
                          {t('fields.response_timeouts_config')}
                        </Divider>
                        <Grid container spacing={6}>
                          <Grid size={{ xs: 6, md: 6 }}>
                            <FormControls>
                              <Typography>{tCommon('common.payin')}</Typography>
                              <FormikNumericField
                                name={'payinCreateResponseTimeout'}
                                label={t('fields.create_order')}
                                allowNegative={false}
                                decimalScale={0}
                                disabled={!isAdminOrTechOperator}
                                defaultValue={0}
                              />
                              <FormikNumericField
                                name={
                                  'payinCreateSyncRequisitesResponseTimeout'
                                }
                                label={t('fields.create_order')}
                                helperText={t('fields.sync_requisites')}
                                allowNegative={false}
                                decimalScale={0}
                                disabled={!isAdminOrTechOperator}
                                defaultValue={0}
                              />
                            </FormControls>
                          </Grid>
                          <Grid size={{ xs: 6, md: 6 }}>
                            <FormControls>
                              <Typography>
                                {tCommon('common.payout')}
                              </Typography>
                              <FormikNumericField
                                name={'payoutCreateOrderResponseTimeout'}
                                label={t('fields.create_order')}
                                allowNegative={false}
                                decimalScale={0}
                                disabled={!isAdminOrTechOperator}
                                defaultValue={0}
                              />
                            </FormControls>
                          </Grid>
                        </Grid>
                      </Fragment>
                    )}
                  </FormControls>
                </Grid>
                {isAdminOrTechOperator && (
                  <Fragment>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Divider sx={{ my: 4 }}>
                        {t('sections.payin_amount_randomizer')}
                      </Divider>
                      <FormControls
                        sx={{ justifyContent: 'space-between' }}
                        row
                      >
                        <FormikCheckbox
                          name="payinAmountRandomizer.enabled"
                          label={t('fields.payin_amount_randomizer_enabled')}
                        />
                        {formik.values.payinAmountRandomizer?.enabled && (
                          <ButtonGroup sx={{ display: 'flex' }}>
                            <Button
                              variant={
                                isPayinAmountRandomizerPercentageMode
                                  ? 'contained'
                                  : 'outlined'
                              }
                              onClick={toggleAmountRandomizerMode}
                              sx={{ flex: 1 }}
                            >
                              {'%'}
                            </Button>
                            <Button
                              variant={
                                !isPayinAmountRandomizerPercentageMode
                                  ? 'contained'
                                  : 'outlined'
                              }
                              onClick={toggleAmountRandomizerMode}
                              sx={{ flex: 1 }}
                            >
                              {shopFiatCurrencyCode}
                            </Button>
                          </ButtonGroup>
                        )}
                      </FormControls>
                      {formik.values.payinAmountRandomizer?.enabled && (
                        <Fragment>
                          <FormControls sx={{ my: 4 }} row>
                            <FormikNumericField
                              name="payinAmountRandomizer.minDivergence"
                              label={t('fields.min_divergence')}
                              fullWidth
                              suffix={payinAmountRandomizerBoundsSuffix}
                            />
                            <FormikNumericField
                              name="payinAmountRandomizer.maxDivergence"
                              label={t('fields.max_divergence')}
                              fullWidth
                              suffix={payinAmountRandomizerBoundsSuffix}
                            />
                            <FormikNumericField
                              name="payinAmountRandomizer.step"
                              label={t('fields.payin_amount_randomizer_step')}
                              fullWidth
                              suffix={payinAmountRandomizerBoundsSuffix}
                            />
                          </FormControls>
                          <FormControls sx={{ my: 4 }} row>
                            <FormikNumericField
                              name="payinAmountRandomizer.minOrderAmount"
                              label={t('fields.min_order_amount')}
                              fullWidth
                              suffix={shopFiatCurrencyCode}
                            />
                            <FormikNumericField
                              name="payinAmountRandomizer.maxOrderAmount"
                              label={t('fields.max_order_amount')}
                              fullWidth
                              suffix={shopFiatCurrencyCode}
                            />
                          </FormControls>
                        </Fragment>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <FormControls>
                        <ShopPayinTradeMethods
                          key={isDataLoaded}
                          fiatCurrencyId={formik.values.fiatCurrencyId}
                          crossFiatCurrencyIds={crossFiatCurrencyIds}
                        />
                      </FormControls>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <FormControls>
                        <ShopPayoutTradeMethods
                          key={isDataLoaded}
                          fiatCurrencyId={formik.values.fiatCurrencyId}
                        />
                      </FormControls>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <FormControls>
                        <ShopP2PProvidersCurrencyExchanges
                          fiatCurrencyId={formik.values.fiatCurrencyId}
                          assetCurrencyIds={getAddedP2PProvidersAssetCurrencies(
                            formik.values,
                          )}
                        />
                        <FormControls sx={{ mb: 2 }}>
                          <ButtonGroup sx={{ display: 'flex' }}>
                            <Button
                              variant={
                                !isCascadeMode ? 'contained' : 'outlined'
                              }
                              onClick={handleToggleCascadeMode}
                              sx={{ flex: 1 }}
                            >
                              {t('sections.p2p_providers.title')}
                            </Button>
                            <Button
                              variant={isCascadeMode ? 'contained' : 'outlined'}
                              onClick={handleToggleCascadeMode}
                              sx={{ flex: 1 }}
                            >
                              {t('fields.cascade')}
                            </Button>
                          </ButtonGroup>
                        </FormControls>

                        {isCascadeMode && (
                          <Fragment>
                            <FormikSelect
                              label={t('fields.cascade')}
                              name="cascadeId"
                              options={cascadeOptions}
                              size="small"
                              sx={{ mb: 2 }}
                            />
                            {showPayoutCascadeTimeoutTotalWarning && (
                              <Alert severity="warning">
                                {t('alerts.payout_cascade_timeout_warning')}
                              </Alert>
                            )}
                          </Fragment>
                        )}

                        {!isCascadeMode && (
                          <ShopP2PProviders
                            assetCurrencyIds={getAddedP2PProvidersAssetCurrencies(
                              formik.values,
                            )}
                            tradeMethodsIds={map(
                              [
                                ...(formik.values.payinTradeMethodsConfigs ||
                                  []),
                                ...(formik.values.payoutTradeMethodsConfigs ||
                                  []),
                              ],
                              (tradeMethod) => tradeMethod.tradeMethodId,
                            )}
                          />
                        )}
                      </FormControls>
                    </Grid>
                    {!isNew && (
                      <Grid size={{ xs: 12, md: 12 }}>
                        <FormControls>
                          <ShopPayinGroups groups={payinGroups} />
                          <ShopPayoutGroups groups={payoutGroups} />
                        </FormControls>
                      </Grid>
                    )}
                  </Fragment>
                )}
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControls>
                    <CustomerFieldsPayin paymentTypes={paymentTypes} />
                    <Divider />

                    <FormControl>
                      <FormikCheckbox
                        name="collectCustomerReceipts"
                        label={t('fields.collect_customer_receipts')}
                      />
                    </FormControl>

                    <MaxParallelCustomerOrdersField />

                    {isAdminOrTechOperator && (
                      <Fragment>
                        <FormControl>
                          <FormLabel>
                            {t('fields.customer_data_collection_order')}
                          </FormLabel>
                          <Field
                            component={RadioGroup}
                            name="customerDataCollectionOrder"
                            row
                          >
                            {Object.values(CustomerDataCollectionOrder).map(
                              (order) => (
                                <FormControlLabel
                                  key={order}
                                  value={order}
                                  control={<Radio />}
                                  label={shopUtils.getCustomerDataCollectionOrder(
                                    order,
                                  )}
                                />
                              ),
                            )}
                          </Field>
                        </FormControl>
                        <PayinBlockCustomers />
                        <PayinBlockAmounts />
                        <FormControl>
                          <FormikCheckbox
                            name="payinTrustedCustomersOnly"
                            label={t('fields.payin_trusted_customers_only')}
                          />
                        </FormControl>
                        {showMissedMappingOverride && (
                          <FormControl
                            sx={{ flexDirection: 'row', alignItems: 'center' }}
                          >
                            <FormikNullableBooleanCheckbox
                              name="failOnMissedMapping"
                              label={t('fields.fail_on_missed_mapping')}
                              data-testid="shop-fail-on-missed-mapping-checkbox"
                              sx={{ marginRight: 0 }}
                            />
                            <HelpTooltip
                              iconTestId="shop-fail-on-missed-mapping-tooltip"
                              title={t(
                                'fields.fail_on_missed_mapping_description',
                                {
                                  defaultValue: missedMappingGlobalDefaultLabel,
                                },
                              )}
                            />
                          </FormControl>
                        )}
                        <FormControl>
                          <FormikCheckbox
                            name="fullWebhook"
                            label={t('fields.full_webhook')}
                          />
                        </FormControl>

                        <FormControl
                          sx={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                          <FormikCheckbox
                            name="payinDistributionSameRequisites"
                            label={t(
                              'fields.restrictions.payin_distribution_same_requisites',
                            )}
                            sx={{ marginRight: 0 }}
                          />
                          <HelpTooltip
                            title={t(
                              'fields.restrictions.payin_distribution_same_requisites_helper',
                            )}
                          />
                        </FormControl>
                        <FormControl
                          sx={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                          <FormikCheckbox
                            name="distributionDebug"
                            label={t('fields.distribution_debug')}
                            sx={{ marginRight: 0 }}
                          />
                          <HelpTooltip
                            title={t('fields.distribution_debug_helper')}
                          />
                        </FormControl>
                        <FormControl
                          sx={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                          <FormikCheckbox
                            name="payinNSPKInstantSyncRequisites"
                            label={t(
                              'fields.payin_nspk_instant_sync_requisites',
                            )}
                            sx={{ marginRight: 0 }}
                          />
                          <HelpTooltip
                            title={t(
                              'fields.payin_nspk_instant_sync_requisites_helper',
                            )}
                          />
                        </FormControl>
                        <FormControl
                          sx={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                          <FormikCheckbox
                            name="useProviderPaymentUrl"
                            label={t('fields.use_provider_payment_url')}
                            sx={{ marginRight: 0 }}
                          />
                          <HelpTooltip
                            title={t('fields.use_provider_payment_url_helper')}
                          />
                        </FormControl>

                        <Fragment>
                          <Typography variant="subtitle1">
                            {t('fields.payin_requisites_fallback')}
                            <HelpTooltip
                              title={t(
                                'fields.payin_requisites_fallback_description',
                              )}
                            />
                          </Typography>
                          <FormControl>
                            <FormikCheckbox
                              name="payinTSBPFallback"
                              label={t('fields.payin_tsbp_fallback')}
                            />
                          </FormControl>
                          <FormControl>
                            <FormikCheckbox
                              name="payinTCard2CardFallback"
                              label={t('fields.payin_tcard2card_fallback')}
                            />
                          </FormControl>
                          <FormControl>
                            <FormikCheckbox
                              name="payinTSimFallback"
                              label={t('fields.payin_tsim_fallback')}
                            />
                          </FormControl>
                          <FormControl>
                            <FormikCheckbox
                              name="payinTNSPKFallback"
                              label={t('fields.payin_tnspk_fallback')}
                            />
                          </FormControl>
                        </Fragment>
                        {isMultitransferEnabled && (
                          <Fragment>
                            <Typography variant="subtitle1">
                              {t('fields.payin_requisites_transform')}
                              <HelpTooltip
                                title={t(
                                  'fields.payin_requisites_transform_description',
                                )}
                              />
                            </Typography>
                            <FormControl>
                              <FormikCheckbox
                                name="payinTSBPNSPKTransform"
                                label={t('fields.payin_tsbp_nspk_transform')}
                              />
                            </FormControl>
                            <FormControl>
                              <FormikCheckbox
                                name="payinTCard2CardNSPKTransform"
                                label={t(
                                  'fields.payin_tcard2card_nspk_transform',
                                )}
                              />
                            </FormControl>
                          </Fragment>
                        )}

                        <Typography variant="subtitle1">
                          {t('fields.restrictions.title')}
                        </Typography>
                        <FormikCheckbox
                          name="isWithdrawalRestricted"
                          label={t('fields.restrictions.withdrawal')}
                        />
                        <FormControl
                          sx={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                          <FormikNumericField
                            name="withdrawalFeeAmount"
                            label={t('fields.withdrawal_fee_amount')}
                            fullWidth
                            suffix={DEFAULT_ASSET_CURRENCY_CODE}
                            disabled={!isAdmin}
                            allowNegative={false}
                            defaultValue={null}
                          />
                          <HelpTooltip
                            title={t('fields.withdrawal_fee_amount_tooltip')}
                          />
                        </FormControl>
                        <FormikCheckbox
                          name="isInternalTransferRestricted"
                          label={t('fields.restrictions.internal_transfer')}
                        />
                        <FormControls>
                          <FormikTextField
                            label={t('fields.comment')}
                            name="comment"
                            multiline
                            rows={3}
                            inputProps={{ maxLength: 1000 }}
                          />
                          {queryResult?.data?.commentedAt && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              className="!tw-mt-2"
                            >
                              {formatUtils.formatDate(
                                queryResult.data.commentedAt,
                              )}
                              {': '}
                              {queryResult.data.commentedBy?.name}
                            </Typography>
                          )}
                        </FormControls>
                      </Fragment>
                    )}
                  </FormControls>
                </Grid>
              </Grid>
              <FormActions>
                <Button variant="outlined" onClick={navigateBack}>
                  {tCommon('buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting || !formik.isValid}
                  onClick={formik.submitForm}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          </div>
        </DataWrapper>
      </FormikProvider>
    </Fragment>
  );
};
